import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { Check, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

export function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }

    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); toast.success("Payment received!"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("failed"); return; }
        if (n >= 15) { setStatus("timeout"); return; }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) { toast.error(extractError(e)); setStatus("error"); }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [location.search]);

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-sage-600 animate-spin" />
            <h1 className="mt-4 text-2xl font-heading font-bold text-sage-900">Confirming payment...</h1>
            <p className="text-sm text-stone2-500 mt-2">Checking with Stripe (attempt {attempts + 1})</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-sage-600 text-white flex items-center justify-center">
              <Check className="w-8 h-8" />
            </div>
            <h1 className="mt-4 text-3xl font-heading font-bold text-sage-900">You're all set</h1>
            <p className="mt-2 text-stone2-600">Payment confirmed. Your sitter will see the booking as paid.</p>
            <button type="button" onClick={() => navigate("/bookings")} data-testid="payment-view-bookings"
              className="btn-primary mt-6">View bookings</button>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-danger text-white flex items-center justify-center">
              <XCircle className="w-8 h-8" />
            </div>
            <h1 className="mt-4 text-2xl font-heading font-bold text-sage-900">Payment not confirmed</h1>
            <p className="mt-2 text-stone2-600">We couldn't confirm your payment. Try again from bookings.</p>
            <Link to="/bookings" className="btn-primary mt-6 inline-flex">Back to bookings</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <XCircle className="w-10 h-10 mx-auto text-ochre-500" />
        <h1 className="mt-4 text-2xl font-heading font-bold text-sage-900">Payment cancelled</h1>
        <p className="mt-2 text-stone2-600">No charges were made. You can try again from bookings.</p>
        <Link to="/bookings" className="btn-primary mt-6 inline-flex">Back to bookings</Link>
      </div>
    </div>
  );
}

export default PaymentSuccess;
